import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCardIcon, ClipboardDocumentListIcon, ArrowDownTrayIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { PDFDownloadLink, Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer';
import Logo from '../../../pages/components/708090.png';
import { auth } from '../../../firebase';
import axios from 'axios';
import { useDriver } from '../../../contexts/DriverContext';
import InvoiceDataModal from '../modals/InvoiceDataModal';

const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/i;

const companyDetails = {
  name: 'Servicegest',
  address: 'Bvd. Eroilor 43, Cluj-Napoca, România',
  phone: '+40742518064',
  email: 'office@servicegest.ro',
  website: 'servicegest.ro',
};

const pdfStyles = StyleSheet.create({
  page: {
    backgroundColor: '#f9f5ff',
    padding: 24,
    fontFamily: 'Helvetica',
    fontSize: 12,
    color: '#222',
  },
  header: {
    backgroundColor: '#7c3aed',
    color: '#fff',
    padding: 16,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  logo: {
    width: 48,
    height: 48,
    marginRight: 16,
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  companyInfo: {
    flexDirection: 'column',
    justifyContent: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 12,
    marginBottom: 2,
  },
  sectionTitle: {
    color: '#7c3aed',
    fontWeight: 'bold',
    fontSize: 16,
    marginBottom: 8,
    marginTop: 12,
  },
  table: {
    width: '100%',
    marginBottom: 12,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #d1d5db',
    alignItems: 'center',
  },
  tableCell: {
    flex: 1,
    padding: 4,
  },
  odometerCell: {
    color: '#7c3aed',
    fontWeight: 'bold',
  },
  footer: {
    backgroundColor: '#ede9fe',
    color: '#7c3aed',
    textAlign: 'center',
    padding: 10,
    borderRadius: 8,
    fontSize: 11,
    marginTop: 16,
  },
});

const ServicegestPDF = ({ result }) => {
  const fullInfo = result.fullInfo || result;
  const interventions = fullInfo.historicalOdometers?.autopassDataSnapshot?.interventions || [];

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <Image src={Logo} style={pdfStyles.logo} />
          <View style={{ flexDirection: 'column', justifyContent: 'center' }}>
            <Text style={pdfStyles.title}>Servicegest - Raport VIN</Text>
            <Text style={pdfStyles.subtitle}>CUI: 51418378</Text>
            <Text style={pdfStyles.subtitle}>{companyDetails.address}</Text>
            <Text style={pdfStyles.subtitle}>{companyDetails.phone}</Text>
            <Text style={pdfStyles.subtitle}>{companyDetails.email}</Text>
            <Text style={pdfStyles.subtitle}>{companyDetails.website}</Text>
          </View>
        </View>
        {/* Vehicle Info */}
        <Text style={pdfStyles.sectionTitle}>Detalii vehicul</Text>
        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableRow}><Text style={pdfStyles.tableCell}>VIN:</Text><Text style={pdfStyles.tableCell}>{result.vin}</Text></View>
          <View style={pdfStyles.tableRow}><Text style={pdfStyles.tableCell}>Marca & Model:</Text><Text style={pdfStyles.tableCell}>{fullInfo.make} {fullInfo.commercialName}</Text></View>
          <View style={pdfStyles.tableRow}><Text style={pdfStyles.tableCell}>An fabricatie:</Text><Text style={pdfStyles.tableCell}>{fullInfo.manufacturingYear || 'N/A'}</Text></View>
          <View style={pdfStyles.tableRow}><Text style={pdfStyles.tableCell}>Caroserie:</Text><Text style={pdfStyles.tableCell}>{fullInfo.body || 'N/A'}</Text></View>
          <View style={pdfStyles.tableRow}><Text style={pdfStyles.tableCell}>Culoare:</Text><Text style={pdfStyles.tableCell}>{fullInfo.colour || 'N/A'}</Text></View>
          <View style={pdfStyles.tableRow}><Text style={pdfStyles.tableCell}>Capacitate motor:</Text><Text style={pdfStyles.tableCell}>{fullInfo.engineCapacity ? `${fullInfo.engineCapacity} cm³` : 'N/A'}</Text></View>
          <View style={pdfStyles.tableRow}><Text style={pdfStyles.tableCell}>Putere motor:</Text><Text style={pdfStyles.tableCell}>{fullInfo.engineMaxPower ? `${fullInfo.engineMaxPower} kW` : 'N/A'}</Text></View>
          <View style={pdfStyles.tableRow}><Text style={pdfStyles.tableCell}>Combustibil:</Text><Text style={pdfStyles.tableCell}>{fullInfo.fuel || 'N/A'}</Text></View>
          <View style={pdfStyles.tableRow}><Text style={pdfStyles.tableCell}>Tara de origine:</Text><Text style={pdfStyles.tableCell}>{fullInfo.countryOfOrigin || 'N/A'}</Text></View>
          {fullInfo.historicalOdometers?.autopassDataSnapshot?.registrationNumber && (
            <View style={pdfStyles.tableRow}>
              <Text style={pdfStyles.tableCell}>Nr. înmatriculare:</Text>
              <Text style={pdfStyles.tableCell}>{fullInfo.historicalOdometers.autopassDataSnapshot.registrationNumber}</Text>
            </View>
          )}
        </View>
        {/* Odometer History */}
        {interventions.length > 0 && (
          <>
            <Text style={pdfStyles.sectionTitle}>Istoric kilometraj</Text>
            <View style={pdfStyles.table}>
              <View style={[pdfStyles.tableRow, { backgroundColor: '#ede9fe' }]}>
                <Text style={pdfStyles.tableCell}>Data</Text>
                <Text style={pdfStyles.tableCell}>Kilometraj</Text>
                <Text style={pdfStyles.tableCell}>Cod interventie</Text>
              </View>
              {interventions.map((entry, idx) => (
                <View style={pdfStyles.tableRow} key={idx}>
                  <Text style={pdfStyles.tableCell}>{entry.date}</Text>
                  <Text style={[pdfStyles.tableCell, pdfStyles.odometerCell]}>{entry.nrKm?.toLocaleString()} km</Text>
                  <Text style={pdfStyles.tableCell}>{entry.code?.join(', ') || 'N/A'}</Text>
                </View>
              ))}
            </View>
          </>
        )}
        {/* Footer */}
        <Text style={pdfStyles.footer}>Raport generat de Servicegest | {result.generatedAt || new Date().toLocaleDateString('ro-RO')}</Text>
      </Page>
    </Document>
  );
};

const DriverVerifyKmPage = ({ userData }) => {
  const { t } = useTranslation();
  const [vin, setVin] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [vinCheckHistory, setVinCheckHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportForModal, setSelectedReportForModal] = useState(null);
  const { driverAccount, refreshDriverAccount } = useDriver();
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Fetch VIN check history on component mount
  useEffect(() => {
    fetchVinCheckHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPage, searchQuery]);

  // Fetch VIN check history
  const fetchVinCheckHistory = async () => {
    setLoadingHistory(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const params = new URLSearchParams({
        page: historyPage.toString(),
        limit: historyLimit.toString(),
      });

      if (searchQuery) {
        params.append('query', searchQuery);
      }

      const response = await axios.get(
        `https://api.servicegest.ro/driver/vin-check/list?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data && Array.isArray(response.data)) {
        setVinCheckHistory(response.data);
      }
    } catch (error) {
      console.error('Error fetching VIN check history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setReport(null);

    // Check if invoice data is complete before processing payment
    // if (!hasInvoiceData) {
    //   setShowInvoiceModal(true);
    //   return;
    // }

    if (!VIN_REGEX.test(vin)) {
      setError(t('verifyKm.vinPlaceholder'));
      return;
    }
    setLoading(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();

      console.log('Payment request:', { vin });

      const response = await fetch(
        'https://api.servicegest.ro/driver/vin-check/payment',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ vin })
        }
      );

      console.log('Payment response status:', response.status);

      if (response.status === 200) {
        const data = await response.json();
        // Payment successful, report returned
        setReport(data);
        setError('');
        // Refresh driver account balance
        if (refreshDriverAccount) {
          await refreshDriverAccount();
        }
        fetchVinCheckHistory();
      } else if (response.status === 402) {
        // Payment required
        setError(t('verifyKm.paymentRequired', 'Plata necesară pentru acest raport VIN'));
      } else {
        const errorData = await response.text();
        setError(errorData || t('verifyKm.processing'));
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.message || t('verifyKm.processing'));
    } finally {
      setLoading(false);
    }
  };

  // Load a report from history - Show in modal
  const loadHistoryReport = (historyItem) => {
    setSelectedReportForModal(historyItem);
    setShowReportModal(true);
  };

  const KmRechartsLine = ({ data }) => {
    const interventions = data?.fullInfo?.historicalOdometers?.autopassDataSnapshot?.interventions ||
      data?.historicalOdometers?.autopassDataSnapshot?.interventions || [];

    if (!interventions || interventions.length < 2) return null;

    const sortedData = [...interventions].sort((a, b) => new Date(a.date) - new Date(b.date));
    const chartData = sortedData.map(d => ({ date: d.date, value: d.nrKm }));

    return (
      <ResponsiveContainer width="100%" height={80}>
        <LineChart data={chartData} margin={{ left: 10, right: 10, top: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis dataKey="value" tick={{ fontSize: 11 }} width={40} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
          <Tooltip formatter={(v) => `${v.toLocaleString()} km`} labelFormatter={d => `Data: ${d}`} />
          <Line type="monotone" dataKey="value" stroke="#a21caf" strokeWidth={3.5} dot={{ r: 4, stroke: "#a21caf", fill: "#fff" }} activeDot={{ r: 6 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* VIN Check Form - Left Side */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          {/* Header */}
          <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <ClipboardDocumentListIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {t('verifyKm.title')}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('verifyKm.subtitle', 'Verifică istoricul kilometrajului vehiculului tău')}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="vin" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('verifyKm.vinLabel')}
                </label>
                <input
                  type="text"
                  id="vin"
                  value={vin}
                  onChange={e => setVin(e.target.value.toUpperCase())}
                  placeholder={t('verifyKm.vinPlaceholder')}
                  className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white text-base transition-colors"
                  maxLength={17}
                  minLength={17}
                  required
                  disabled={loading}
                />
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">
                      {t('verifyKm.reportPrice')}
                    </span>
                    <span className="ml-2 text-lg text-purple-600 dark:text-purple-400 font-bold">
                      {t('verifyKm.priceValue')} din sold
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">
                    {t('verifyKm.dataSource')}
                  </div>
                </div>
                <div className="text-xs mt-3 text-slate-600 dark:text-slate-400">
                  {t('verifyKm.dataNote')}
                </div>
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-red-700 dark:text-red-400 text-sm text-center">{error}</p>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-slate-800 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                  disabled={loading}
                >
                  <CreditCardIcon className="h-5 w-5" />
                  {loading ? t('verifyKm.processing') : t('verifyKm.payAndVerify')}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* VIN Check History - Right Side */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <ClipboardDocumentListIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {t('verifyKm.history', 'Istoricul rapoartelor')}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t('verifyKm.previousReports')}
                </p>
              </div>
            </div>
          </div>

          <div className="p-2">
            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('verifyKm.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-slate-700 dark:text-white text-sm"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
              </div>
            </div>

            {/* History List */}
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent"></div>
                <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">{t('verifyKm.loading')}</span>
              </div>
            ) : vinCheckHistory.length > 0 ? (
              <div className="space-y-3">
                {vinCheckHistory.map((item) => (
                  <div
                    key={item.vin + item.generatedAt}
                    className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                    onClick={() => loadHistoryReport(item)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900 dark:text-white mb-1">
                          {item.vin}
                        </p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <span>
                            {item.fullInfo?.make} {item.fullInfo?.commercialName}
                          </span>
                          {item.fullInfo?.manufacturingYear && (
                            <span className="hidden sm:inline">•</span>
                          )}
                          {item.fullInfo?.manufacturingYear && (
                            <span>{t('verifyKm.manufacturingYear')} {item.fullInfo.manufacturingYear}</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                          {t('verifyKm.generatedAt')}: {item.generatedAt}
                        </p>
                      </div>
                      <ArrowDownTrayIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 ml-2" />
                    </div>

                    {/* {item.fullInfo?.historicalOdometers?.autopassDataSnapshot?.interventions && (
                      <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                        <p className="text-xs text-slate-600 dark:text-slate-400">
                          {item.fullInfo.historicalOdometers.autopassDataSnapshot.interventions.length} înregistrări de kilometraj
                        </p>
                      </div>
                    )} */}
                  </div>
                ))}

                {/* Pagination */}
                {vinCheckHistory.length >= historyLimit && (
                  <div className="flex justify-center gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <button
                      onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                      disabled={historyPage === 1}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${historyPage === 1
                        ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                        : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                        }`}
                    >
                      ← {t('verifyKm.previous')}
                    </button>
                    <span className="flex items-center px-3 text-sm text-slate-600 dark:text-slate-400">
                      {t('verifyKm.page')} {historyPage}
                    </span>
                    <button
                      onClick={() => setHistoryPage(prev => prev + 1)}
                      disabled={vinCheckHistory.length < historyLimit}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${vinCheckHistory.length < historyLimit
                        ? 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                        : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600'
                        }`}
                    >
                      {t('verifyKm.next')} →
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                <p className="text-slate-500 dark:text-slate-400">
                  {searchQuery ? t('verifyKm.noReportsFound') : t('verifyKm.noReportsGenerated')}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-2 text-sm text-purple-600 dark:text-purple-400 hover:underline"
                  >
                    {t('verifyKm.clearFilter')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full Width Report Display */}
      {report && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                {t('verifyKm.mileageReport')}
              </h3>
              <PDFDownloadLink
                document={<ServicegestPDF result={report} />}
                fileName={`Raport_Kilometraj_${report.vin}.pdf`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium shadow-sm transition-colors"
              >
                {({ loading }) => (
                  <>
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    {loading ? t('verifyKm.generating') : t('verifyKm.downloadPDF')}
                  </>
                )}
              </PDFDownloadLink>
            </div>
          </div>

          <div className="p-2">
            <div className="bg-white dark:bg-slate-700/50 rounded-lg p-5 border border-slate-200 dark:border-slate-600 shadow-sm">
              <div className="mb-4 pb-3 border-b border-slate-200 dark:border-slate-600">
                <div className="flex flex-col sm:flex-row sm:items-center break-all">
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">{t('verifyKm.vin')}:</span>
                  <span className="sm:ml-2 text-sm sm:text-base font-semibold text-slate-900 dark:text-white break-all">{report.vin}</span>
                </div>
                {report.generatedAt && (
                  <div className="mt-2">
                    <div className="flex flex-col sm:flex-row sm:items-center">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">{t('verifyKm.generatedAt')}:</span>
                      <span className="sm:ml-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300">{report.generatedAt}</span>
                    </div>
                  </div>
                )}
              </div>

              <KmRechartsLine data={report} />

              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-600">
                      <th className="text-left py-2 px-2 text-slate-700 dark:text-slate-300 font-semibold text-xs">{t('verifyKm.date')}</th>
                      <th className="text-left py-2 px-2 text-slate-700 dark:text-slate-300 font-semibold text-xs">{t('verifyKm.mileage')}</th>
                      <th className="text-left py-2 px-2 text-slate-700 dark:text-slate-300 font-semibold text-xs">{t('verifyKm.interventionCode')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.fullInfo?.historicalOdometers?.autopassDataSnapshot?.interventions || []).map((row, idx) => (
                      <tr key={idx} className="border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="py-2 px-2 text-slate-700 dark:text-slate-300">{row.date}</td>
                        <td className="py-2 px-2 font-mono text-purple-600 dark:text-purple-400 font-semibold">{row.nrKm.toLocaleString()} km</td>
                        <td className="py-2 px-2 text-slate-600 dark:text-slate-400">{row.code?.join(', ') || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-600">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('verifyKm.dataDisclaimer')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Detail Modal */}
      {showReportModal && selectedReportForModal && (
        <div className="fixed inset-0 z-50 -top-20 flex items-center justify-center p-4 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <ClipboardDocumentListIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                      {t('verifyKm.mileageReport')}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      VIN: {selectedReportForModal.vin}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-2">
              <div className="bg-white dark:bg-slate-700/50 rounded-lg p-5 border border-slate-200 dark:border-slate-600 shadow-sm">
                {/* Vehicle Info */}
                <div className="mb-4 pb-3 border-b border-slate-200 dark:border-slate-600">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="break-all">
                      <div className="flex flex-col sm:flex-row sm:items-center">
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">{t('verifyKm.vin')}:</span>
                        <span className="sm:ml-2 text-sm sm:text-base font-semibold text-slate-900 dark:text-white break-all">
                          {selectedReportForModal.vin}
                        </span>
                      </div>
                    </div>
                    <div className="break-words">
                      <div className="flex flex-col sm:flex-row sm:items-center">
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">{t('verifyKm.vehicle')}:</span>
                        <span className="sm:ml-2 text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                          {selectedReportForModal.fullInfo?.make} {selectedReportForModal.fullInfo?.commercialName}
                        </span>
                      </div>
                    </div>
                    {selectedReportForModal.fullInfo?.manufacturingYear && (
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">{t('verifyKm.manufacturingYear')}:</span>
                          <span className="sm:ml-2 text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                            {selectedReportForModal.fullInfo.manufacturingYear}
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedReportForModal.fullInfo?.engineCapacity && (
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">Capacitate motor:</span>
                          <span className="sm:ml-2 text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                            {selectedReportForModal.fullInfo.engineCapacity} cm³
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedReportForModal.fullInfo?.engineMaxPower && (
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">Putere motor:</span>
                          <span className="sm:ml-2 text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                            {selectedReportForModal.fullInfo.engineMaxPower} kW
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedReportForModal.fullInfo?.fuel && (
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">Combustibil:</span>
                          <span className="sm:ml-2 text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                            {selectedReportForModal.fullInfo.fuel}
                          </span>
                        </div>
                      </div>
                    )}
                    {selectedReportForModal.generatedAt && (
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">{t('verifyKm.generatedAt')}:</span>
                          <span className="sm:ml-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                            {selectedReportForModal.generatedAt}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Chart */}
                <KmRechartsLine data={selectedReportForModal} />

                {/* Data Table */}
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-600">
                        <th className="text-left py-2 px-2 text-slate-700 dark:text-slate-300 font-semibold text-xs">
                          {t('verifyKm.date')}
                        </th>
                        <th className="text-left py-2 px-2 text-slate-700 dark:text-slate-300 font-semibold text-xs">
                          {t('verifyKm.mileage')}
                        </th>
                        <th className="text-left py-2 px-2 text-slate-700 dark:text-slate-300 font-semibold text-xs">
                          {t('verifyKm.interventionCode')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedReportForModal.fullInfo?.historicalOdometers?.autopassDataSnapshot?.interventions || []).map((row, idx) => (
                        <tr key={idx} className="border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                          <td className="py-2 px-2 text-slate-700 dark:text-slate-300">{row.date}</td>
                          <td className="py-2 px-2 font-mono text-purple-600 dark:text-purple-400 font-semibold">
                            {row.nrKm.toLocaleString()} km
                          </td>
                          <td className="py-2 px-2 text-slate-600 dark:text-slate-400">
                            {row.code?.join(', ') || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Disclaimer */}
                <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-600">
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('verifyKm.dataDisclaimer')}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 flex gap-3">
                <PDFDownloadLink
                  document={<ServicegestPDF result={selectedReportForModal} />}
                  fileName={`Raport_Kilometraj_${selectedReportForModal.vin}.pdf`}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium shadow-sm transition-colors"
                >
                  {({ loading }) => (
                    <>
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {loading ? t('verifyKm.generating') : t('verifyKm.downloadPDF')}
                    </>
                  )}
                </PDFDownloadLink>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="px-6 py-3 rounded-lg border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  {t('verifyKm.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Data Modal */}
      {showInvoiceModal && (
        <InvoiceDataModal
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          onSuccess={async () => {
            await refreshDriverAccount();
            setShowInvoiceModal(false);
            // Continue with the VIN verification after invoice data is saved
          }}
          driverAccount={driverAccount}
        />
      )}
    </div>
  );
};

export default DriverVerifyKmPage;
